import { useState, useRef, useEffect } from 'react';
import { Dna, Menu, X, ChevronDown, LogOut } from 'lucide-react';

const navItems = [
  { id: 'home', label: 'Home' },
  { id: 'howit-works', label: 'How It Works' },
  { id: 'analysis', label: 'Analysis' },
  { id: 'tools', label: 'Tools' },
  { id: 'faqs', label: 'FAQs' },
  { id: 'feedback', label: 'Feedback' },
];

const aboutItems = [
  { id: 'about', label: 'About CryMiner' },
  { id: 'about-oneomics', label: 'About ONEOMICS' },
];

// Keep in sync with PROTECTED_TABS in App.jsx — this list only controls whether
// the link is *shown*; App.jsx's list is what actually blocks the page.
const PROTECTED_NAV_IDS = []; 
const NavigationMenu = ({ activeTab, setActiveTab, isAuthenticated, user, onLogout }) => {
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const [aboutOpen, setAboutOpen] = useState(false);
  const dropdownRef = useRef(null);

  const handleTabChange = (id) => {
    if (typeof setActiveTab !== 'function') {
      console.error('NavigationMenu: setActiveTab prop is missing or not a function');
      return;
    }
    setActiveTab(id);
  };

  useEffect(() => {
    const handler = (e) => {
      if (dropdownRef.current && !dropdownRef.current.contains(e.target)) {
        setAboutOpen(false);
      }
    };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, []);

  const isAboutActive = activeTab === 'about' || activeTab === 'about-oneomics';

  const visibleNavItems = navItems.filter(
    (item) => isAuthenticated || !PROTECTED_NAV_IDS.includes(item.id)
  );

  return (
    <nav className="bg-white/90 backdrop-blur-md border-b border-slate-200 sticky top-0 z-50">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="flex justify-between items-center h-16">
          {/* Logo */}
<div
  className="flex items-center space-x-3 group cursor-pointer"
  onClick={() => handleTabChange('home')}
>
  <img
    src="/CRYMINER.jpg__1_-removebg-preview.png"
    
    className="h-10 w-auto group-hover:scale-105 transition-transform duration-300"
  />
  <div className="font-display text-xl font-extrabold text-brand"></div>
</div>

          {/* Desktop Nav */}
          <div className="hidden md:flex items-center space-x-1">
            {/* Home */}
            <button
              onClick={() => handleTabChange('home')}
              className={`relative px-3 py-2 text-sm font-semibold transition-all duration-300 group ${activeTab === 'home' ? 'text-brand' : 'text-slate-600 hover:text-brand'
                }`}
            >
              Home
              <span
                className={`absolute left-0 -bottom-0.5 h-0.5 rounded-full bg-brand transition-all duration-300 ${activeTab === 'home' ? 'w-full' : 'w-0 group-hover:w-full'
                  }`}
              ></span>
            </button>

            {/* About Dropdown — immediately after Home */}
            <div className="relative" ref={dropdownRef}>
              <button
                onClick={() => setAboutOpen((o) => !o)}
                className={`relative px-3 py-2 text-sm font-semibold transition-all duration-300 flex items-center gap-1 group ${isAboutActive ? 'text-brand' : 'text-slate-600 hover:text-brand'
                  }`}
              >
                About
                <ChevronDown
                  className={`w-4 h-4 transition-transform duration-200 ${aboutOpen ? 'rotate-180' : ''}`}
                />
                <span
                  className={`absolute left-0 -bottom-0.5 h-0.5 rounded-full bg-brand transition-all duration-300 ${isAboutActive ? 'w-full' : 'w-0 group-hover:w-full'
                    }`}
                ></span>
              </button>

              {aboutOpen && (
                <div className="absolute right-0 mt-1 w-48 bg-white rounded-xl border border-slate-200 shadow-lg py-1 z-50 animate-slide-down">
                  {aboutItems.map((item) => (
                    <button
                      key={item.id}
                      onClick={() => {
                        handleTabChange(item.id);
                        setAboutOpen(false);
                      }}
                      className={`w-full text-left px-4 py-2.5 text-sm font-medium transition-colors ${activeTab === item.id
                          ? 'text-brand bg-brand-tint'
                          : 'text-slate-600 hover:text-brand hover:bg-slate-50'
                        }`}
                    >
                      {item.label}
                    </button>
                  ))}
                </div>
              )}
            </div>

            {/* Remaining nav items (skip Home, it's already rendered; Analysis/Tools hidden when logged out) */}
            {visibleNavItems.slice(1).map((item) => (
              <button
                key={item.id}
                onClick={() => handleTabChange(item.id)}
                className={`relative px-3 py-2 text-sm font-semibold transition-all duration-300 group ${activeTab === item.id ? 'text-brand' : 'text-slate-600 hover:text-brand'
                  }`}
              >
                {item.label}
                <span
                  className={`absolute left-0 -bottom-0.5 h-0.5 rounded-full bg-brand transition-all duration-300 ${activeTab === item.id ? 'w-full' : 'w-0 group-hover:w-full'
                    }`}
                ></span>
              </button>
            ))}
          </div>

          {/* Auth buttons / logged-in state */}
          <div className="hidden md:flex items-center space-x-3">
            {isAuthenticated ? (
              <>
                <span className="text-sm text-slate-500 truncate max-w-[140px]">
                  Hi, {user?.name?.split(' ')[0] || 'there'}
                </span>
                <button
                  onClick={onLogout}
                  className="flex items-center gap-1.5 px-4 py-2 text-sm text-slate-600 hover:text-brand transition-colors"
                >
                  <LogOut className="w-4 h-4" />
                  Logout
                </button>
              </>
            ) : (
              <>
                <button
                  onClick={() => handleTabChange('login')}
                  className="px-4 py-2 text-sm text-slate-600 hover:text-brand transition-colors"
                >
                  Login
                </button>
                <button
                  onClick={() => handleTabChange('register')}
                  className="brand-btn px-4 py-2 rounded-lg text-sm font-semibold hover:scale-105 transition-transform duration-300"
                >
                  Register
                </button>
              </>
            )}
          </div>

          {/* Mobile hamburger */}
          <button
            onClick={() => setIsMenuOpen(!isMenuOpen)}
            className="md:hidden text-slate-700 hover:text-brand transition-transform active:scale-90"
          >
            {isMenuOpen ? <X className="w-6 h-6" /> : <Menu className="w-6 h-6" />}
          </button>
        </div>
      </div>

      {/* Mobile Menu — About items right after Home */}
      {isMenuOpen && (
        <div className="md:hidden bg-white border-t border-slate-200 animate-slide-down">
          <div className="px-2 pt-2 pb-3 space-y-1">
            {[
              visibleNavItems[0], // Home
              ...aboutItems,
              ...visibleNavItems.slice(1),
              ...(isAuthenticated ? [] : [{ id: 'login', label: 'Login' }, { id: 'register', label: 'Register' }]),
            ].map((item, i) => (
              <button
                key={item.id}
                onClick={() => {
                  handleTabChange(item.id);
                  setIsMenuOpen(false);
                }}
                className={`block w-full text-left px-3 py-2 text-sm font-medium rounded-lg transition-colors animate-fade-in-up ${activeTab === item.id
                    ? 'text-brand bg-brand-tint'
                    : 'text-slate-600 hover:text-brand hover:bg-slate-50'
                  }`}
                style={{ animationDelay: `${i * 40}ms` }}
              >
                {item.label}
              </button>
            ))}
            {isAuthenticated && (
              <button
                onClick={() => {
                  onLogout();
                  setIsMenuOpen(false);
                }}
                className="block w-full text-left px-3 py-2 text-sm font-medium rounded-lg text-slate-600 hover:text-brand hover:bg-slate-50 transition-colors"
              >
                Logout ({user?.name?.split(' ')[0] || 'account'})
              </button>
            )}
          </div>
        </div>
      )}
    </nav>
  );
};

export default NavigationMenu;